import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import { useSettings } from '../contexts/SettingsContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';
import TradesTable from '../components/TradesTable';
import TradeFilters from '../components/common/TradeFilters';
import useTradeFilters from '../hooks/useTradeFilters';
import StrategyPerformance from '../components/strategies/StrategyPerformance';
import WatchlistSymbolPerformance from '../components/symbols/WatchlistSymbolPerformance';
// Floating chat is now used instead of inline ChatBot

const COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3'];

const DashboardPage = () => {
  const [trades, setTrades] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  const theme = useTheme();
  const { refreshSettings } = useSettings();
  
  const {
    selectedCategory, selectedSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol, setSelectedStrategy, setSelectedStatus, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, statuses, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  useEffect(() => {
    fetchTrades();
    fetchStrategies();
    refreshSettings(); // Fetch user settings including plan details
    
    // Listen for trade creation events
    const handleTradeCreated = () => {
      fetchTrades();
    };
    
    window.addEventListener('tradeCreated', handleTradeCreated);
    
    return () => {
      window.removeEventListener('tradeCreated', handleTradeCreated);
    };
  }, [refreshSettings]);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(response.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const strategyMap = {};
      for (const strat of response.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategies(response.data);
      setStrategyNamesMap(strategyMap);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const countStatus = (status) => filteredTrades.filter((t) => t.status === status).length;

  const winRate = () => {
    const wins = countStatus('WIN');
    const losses = countStatus('LOSS');
    const total = wins + losses;
    return total > 0 ? ((wins / total) * 100).toFixed(1) + '%' : 'N/A';
  };

  const totalPnL = filteredTrades.reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0);
  // Calculate average risk-reward ratio
  const avgRiskRewardRaw = filteredTrades.filter(t => t.riskRewardRatio && parseFloat(t.riskRewardRatio) > 0)
    .reduce((sum, t, _, arr) => sum + parseFloat(t.riskRewardRatio) / arr.length, 0);
  
  // Format as 1:X ratio
  const formatRiskRewardRatio = (ratio) => {
    if (!ratio || ratio <= 0) return '1:1';
    return `1:${ratio.toFixed(1)}`.replace('.0', '');
  };
  
  const avgRiskRewardFormatted = formatRiskRewardRatio(avgRiskRewardRaw);

  const tradesPerStrategy = filteredTrades.reduce((acc, trade) => {
    (trade.strategyIds || []).forEach((id) => {
      const name = strategyNamesMap[id] || id;
      acc[name] = (acc[name] || 0) + 1;
    });
    return acc;
  }, {});

  const strategyData = Object.entries(tradesPerStrategy).map(([name, count]) => ({
    name,
    value: count
  }));

  const statusData = [
    { name: 'WIN', value: countStatus('WIN') },
    { name: 'LOSS', value: countStatus('LOSS') },
    { name: 'BREAKEVEN', value: countStatus('BREAKEVEN') },
    { name: 'OPEN', value: countStatus('OPEN') }
  ];

  const performanceData = filteredTrades.map((t, idx) => ({
    name: `${t.symbol} ${dayjs(t.tradeDate).format('MM/DD')}`,
    PnL: parseFloat(t.profitLoss || 0),
    date: t.tradeDate
  }));

  const riskRewardData = filteredTrades.filter(t => t.riskAmount && t.rewardAmount)
    .map(t => ({
      risk: parseFloat(t.riskAmount),
      reward: parseFloat(t.rewardAmount),
      symbol: t.symbol,
      status: t.status
    }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>Dashboard</Typography>

      {/* Header Section - Filters + KPI Cards */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <TradeFilters
          selectedCategory={selectedCategory}
          selectedSymbol={selectedSymbol}
          selectedStrategy={selectedStrategy}
          selectedStatus={selectedStatus}
          selectedTags={selectedTags}
          startDate={startDate}
          endDate={endDate}
          onCategoryChange={setSelectedCategory}
          onSymbolChange={setSelectedSymbol}
          onStrategyChange={setSelectedStrategy}
          onStatusChange={setSelectedStatus}
          onTagsChange={setSelectedTags}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTimeFilter={handleTimeFilter}
          symbols={symbols}
          strategies={strategies.map(s => s.strategyId)}
          statuses={statuses}
          allTags={allTags}
          strategyNamesMap={strategyNamesMap}
        />
        
        {/* KPI Cards Row */}
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center', height: { xs: '80px', sm: '100px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Total Trades</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>{filteredTrades.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Win Rate</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{winRate()}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Total P&L</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }} color={totalPnL >= 0 ? 'success.main' : 'error.main'}>${totalPnL.toFixed(2)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Avg R:R</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{avgRiskRewardFormatted}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Strategy Performance Section */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>📈 Strategy Performance</Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <StrategyPerformance filteredTrades={filteredTrades} onTradeUpdate={fetchTrades} />
      </Paper>

      {/* Symbol Performance Section */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>📊 Symbol Performance</Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <WatchlistSymbolPerformance trades={filteredTrades} onTradeUpdate={fetchTrades} strategies={strategies} />
      </Paper>

      {/* Daily Performance Summary */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>📅 Daily Performance Summary</Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Grid container spacing={2}>
          {Object.entries(
            filteredTrades.reduce((acc, trade) => {
              const date = trade.tradeDate;
              if (!acc[date]) acc[date] = { wins: 0, losses: 0, totalPnL: 0, trades: 0, breakeven: 0, open: 0 };
              if (trade.status === 'WIN') acc[date].wins++;
              else if (trade.status === 'LOSS') acc[date].losses++;
              else if (trade.status === 'BREAKEVEN') acc[date].breakeven++;
              else if (trade.status === 'OPEN') acc[date].open++;
              acc[date].totalPnL += parseFloat(trade.profitLoss || 0);
              acc[date].trades++;
              return acc;
            }, {})
          ).sort(([a], [b]) => new Date(b) - new Date(a)).slice(0, 12).map(([date, data]) => {
            const isProfit = data.totalPnL > 0;
            const isLoss = data.totalPnL < 0;
            const winRate = data.wins + data.losses > 0 ? ((data.wins / (data.wins + data.losses)) * 100).toFixed(0) : 0;
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={date}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: `1px solid ${isProfit ? theme.palette.success.main : isLoss ? theme.palette.error.main : theme.palette.grey[300]}`,
                    borderRadius: 2,
                    '&:hover': { elevation: 3 }
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: isProfit ? 'success.main' : isLoss ? 'error.main' : 'text.primary',
                      mb: 1
                    }}
                  >
                    ${data.totalPnL.toFixed(2)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">{data.trades} trades</Typography>
                    <Typography variant="caption">{winRate}% WR</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Chip label={`W:${data.wins}`} size="small" color="success" variant="outlined" />
                    <Chip label={`L:${data.losses}`} size="small" color="error" variant="outlined" />
                    {data.open > 0 && <Chip label={`O:${data.open}`} size="small" color="info" variant="outlined" />}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* AI Chat Assistant - Now using floating chat bot */}

    </Box>
  );
};

export default DashboardPage;